/**
 * Display quantity for a saved-list / pantry-match ingredient row.
 * Mirrors backend/services/recipe_pantry_match_service._ingredient_qty.
 */

const QTY_KEYS = ['qty', 'quantity', 'amount']

const LEADING_QTY_RE = new RegExp(
  String.raw`^\s*(?:[-*•]\s*)?(?:(?:about|approx\.?|approximately|around|scant|heaping|generous)\s+)?` +
    String.raw`(\d+\s+\d+/\d+|\d+/\d+|\d+(?:\.\d+)?|[¼½¾⅓⅔⅛⅜⅝⅞])` +
    String.raw`(?:\s*(?:-|–|—|to)\s*(?:\d+\s+\d+/\d+|\d+/\d+|\d+(?:\.\d+)?|[¼½¾⅓⅔⅛⅜⅝⅞]))?` +
    String.raw`(?:\s*(?:tablespoons?|teaspoons?|tbsps?|tsps?|cups?|ounces?|oz|pounds?|lbs?|grams?|g|kg|ml|l|cloves?|bunches?|pinches?|dashes?|handfuls?))?\b`,
  'i',
)

function parseLeadingQuantity(text) {
  const raw = String(text || '').trim()
  if (!raw) return ''
  const match = raw.match(LEADING_QTY_RE)
  if (!match) return ''
  const amount = match[1]
  const unitMatch = raw.slice(match.index, match.index + match[0].length).match(
    /\b(tablespoons?|teaspoons?|tbsps?|tsps?|cups?|ounces?|oz|pounds?|lbs?|grams?|g|kg|ml|l|cloves?|bunches?|pinches?|dashes?|handfuls?)\b/i,
  )
  if (!unitMatch) return amount
  const unit = unitMatch[1].toLowerCase()
  const normalized = unit.startsWith('tablespoon') || unit.startsWith('tbsp') || unit === 'tbs'
    ? 'tbsp'
    : unit.startsWith('teaspoon') || unit.startsWith('tsp')
      ? 'tsp'
      : unit.startsWith('ounce') || unit === 'oz'
        ? 'oz'
        : unit.startsWith('pound') || unit === 'lb' || unit === 'lbs'
          ? 'lb'
          : unit.startsWith('gram') || unit === 'g'
            ? 'g'
            : unit
  return `${amount} ${normalized}`
}

/**
 * Strip a leading quantity/unit from an ingredient or grocery line for display/sort.
 * Falls back to the original text when nothing remains after stripping.
 */
export function stripLeadingIngredientQuantity(text) {
  const raw = String(text || '').trim()
  if (!raw) return ''
  const match = raw.match(LEADING_QTY_RE)
  if (!match) return raw
  // Keep product names like "2% milk" (bare number glued to a percent sign).
  const afterMatch = raw.slice(match.index + match[0].length)
  if (afterMatch.startsWith('%')) return raw
  const remainder = afterMatch.replace(/^[\s,.-]+/, '').trim()
  return remainder || raw
}

/** Case-insensitive A–Z sort key; ignores leading qty/units when present. */
export function groceryItemAlphaSortKey(text) {
  return stripLeadingIngredientQuantity(text).toLocaleLowerCase()
}

/**
 * Lookup / metadata-merge key for a shopping-list line.
 * Strips leading qty/units so "3 tbsp sesame seeds" matches "sesame seeds".
 */
export function groceryLookupKey(text) {
  return groceryItemAlphaSortKey(text).trim()
}

export function ingredientDisplayQty(item) {
  if (!item || typeof item !== 'object') return ''
  for (const key of QTY_KEYS) {
    const value = item[key]
    if (value != null && String(value).trim()) return String(value).trim()
  }
  const quantityValue = String(item.quantity_value || '').trim()
  const measure = String(item.measure || '').trim()
  if (quantityValue) return `${quantityValue}${measure ? ` ${measure}` : ''}`.trim()
  const original = String(item.original_ingredient || '').trim()
  if (original) {
    const parsed = parseLeadingQuantity(original)
    if (parsed) return parsed
  }
  const name = String(item.item || item.name || item.item_name || '').trim()
  if (name) {
    const parsed = parseLeadingQuantity(name)
    if (parsed) return parsed
  }
  return ''
}

export function ingredientDisplayName(item) {
  if (!item || typeof item !== 'object') return ''
  const groceryName = String(item.item || item.name || item.item_name || '').trim()
  const original = String(item.original_ingredient || '').trim()
  if (!original) return groceryName
  let remainder = original.replace(/^for the [^:]+:\s*/i, '').trim()
  remainder = stripLeadingIngredientQuantity(remainder).trim()
  if (!remainder) return groceryName
  if (!groceryName) return remainder
  const groceryLower = groceryName.toLowerCase()
  const remainderLower = remainder.toLowerCase()
  if (groceryLower === remainderLower) return groceryName
  if (remainderLower.includes(groceryLower)) return remainder
  const words = groceryLower.split(/[^a-z0-9]+/).filter((w) => w.length > 2)
  if (words.length > 0 && words.every((w) => remainderLower.includes(w)) && remainder.length > groceryName.length) {
    return remainder
  }
  return groceryName
}

export function mapIngredientRow(item, { have = true } = {}) {
  return {
    qty: ingredientDisplayQty(item),
    name: ingredientDisplayName(item),
    have: typeof item.have === 'boolean' ? item.have : have,
  }
}
